import type {
  SpaceReview,
  SpaceReviewsBatchEntry,
} from "../../../../services/reviews.service";
import type { OwnerSpaceReviewsLastViewed } from "../../../../services/spaces.service";
import type { UnreadReviewSpaceCardData } from "../../../types/model/unread-review";
import { buildListingThumbnailUrl } from "../../../utils/cloudinary-image.utils";
import {
  MAX_UNREAD_REVIEW_SPACES,
  UNREAD_REVIEW_THUMBNAIL_PX,
} from "./converter.consts";

type ToUnreadReviewSpaceCardsArgs = {
  batchEntries: SpaceReviewsBatchEntry[] | undefined;
  lastViewedSpaces: OwnerSpaceReviewsLastViewed[] | undefined;
};

/**
 * A review is "new" when it was created after the host last viewed the space's
 * reviews. A null `lastViewedAt` (never viewed) treats every dated review as
 * new.
 */
const isNewReview = (review: SpaceReview, lastViewedAt: string | null): boolean => {
  if (!review.date_created) return false;
  if (!lastViewedAt) return true;
  return (
    new Date(review.date_created).getTime() > new Date(lastViewedAt).getTime()
  );
};

/** Pulls a title + thumbnail off the first review that carries the space media. */
const spaceTitleAndPhoto = (
  reviews: SpaceReview[],
): { photoUrl?: string; title?: string } => {
  for (const review of reviews) {
    const title = review.listing?.title ?? review.space?.title;
    const externalId =
      review.listing?.images?.[0]?.external_id ??
      review.space?.images?.[0]?.external_id;
    if (title || externalId) {
      return {
        photoUrl: buildListingThumbnailUrl({
          externalId,
          size: UNREAD_REVIEW_THUMBNAIL_PX,
        }),
        title,
      };
    }
  }
  return { photoUrl: undefined, title: undefined };
};

/**
 * Cross-references the per-space reviews against each space's
 * `reviews_last_viewed_at` to surface spaces with reviews published since the
 * host last looked. Orders rows alphabetically by space title and caps the
 * result at `MAX_UNREAD_REVIEW_SPACES`.
 */
export const toUnreadReviewSpaceCards = ({
  batchEntries,
  lastViewedSpaces,
}: ToUnreadReviewSpaceCardsArgs): UnreadReviewSpaceCardData[] => {
  if (!batchEntries || !lastViewedSpaces) return [];

  const lastViewedBySpaceId = new Map(
    lastViewedSpaces.map((space) => [
      space.space_id,
      space.reviews_last_viewed_at,
    ]),
  );

  const cards: UnreadReviewSpaceCardData[] = [];
  for (const { reviews, spaceId } of batchEntries) {
    const lastViewedAt = lastViewedBySpaceId.get(spaceId) ?? null;
    const newReviewCount = reviews.filter((review) =>
      isNewReview(review, lastViewedAt),
    ).length;
    if (0 === newReviewCount) continue;

    const { photoUrl, title } = spaceTitleAndPhoto(reviews);
    cards.push({ newReviewCount, photoUrl, spaceId, title });
  }

  return cards
    .sort((a, b) => (a.title ?? "").localeCompare(b.title ?? ""))
    .slice(0, MAX_UNREAD_REVIEW_SPACES);
};
